<template>
  <v-card class="recorder credit-card card--flex--toolbar">
    <v-card-title class="grey lighten-3 pt-0">
      <v-toolbar flat class="transparent">
        <v-toolbar-title>会計処理フォーム</v-toolbar-title>
        <v-row dense align="center" justify="end">
          <span v-if="$vuetify.breakpoint.smAndUp" class="body-1 mr-2">顧客関連</span>
          <v-btn-toggle mandatory dense v-model="form.link">
            <v-btn
              :outlined="!form.link"
              :depressed="form.link"
              color="secondary white--text"
              :value="true">
              <v-icon small>fal fa-link</v-icon>
            </v-btn>
            <v-btn
              :outlined="form.link"
              :depressed="!form.link"
              color="secondary white--text"
              :value="false">
              <v-icon small>fal fa-unlink</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-row>
        <template v-slot:extension v-if="isLink" class="px-0">
          <v-row dense>
            <v-col cols="12" class="mt-5">
              <v-autocomplete validate-on-blur hide-details
                ref="searcher"
                v-model="form.cid"
                color="primary"
                label="関連する顧客"
                :no-data-text="printNoDataText"
                :readonly="isSetClient"
                :items="getClientItems"
                :loading="isSearching"
                :search-input.sync="form.search"
                :rules="rules.required"
              />
            </v-col>
          </v-row>
        </template>
      </v-toolbar>
    </v-card-title>
    <v-card-text class="pt-3">
      <v-form lazy-validation ref="form" v-model="form.valid">
        <v-row dense v-if="form.cid || !isLink" align="center" justify="center">
          <v-col cols="12">
            <v-menu offset-y
              v-model="config.datepicker"
              transition="scale-transition"
              min-width="290px"
              :close-on-content-click="false"
              :nudge-right="40">
              <template v-slot:activator="{ on }">
                <v-text-field readonly validate-on-blur
                  v-on="on"
                  v-model="printFormDate"
                  color="primary"
                  label="カード使用日"
                  :rules="rules.required"
                  :append-icon="form.date ? 'fal fa-times' : null"
                  @click:append="form.date = ''"
                />
              </template>
              <v-date-picker
                v-model="form.date"
                locale="ja"
                color="amber"
                :day-format="date => new Date(date).getUTCDate()"
                @input="config.datepicker = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12">
            <v-text-field validate-on-blur
              v-model="form.payee"
              label="支払先"
              color="primary"
              counter="50"
              :rules="rules.required"
            />
          </v-col>
          <v-col cols="12">
            <v-combobox multiple validate-on-blur
              v-model="form.items"
              label="使用目的"
              color="primary"
              :items="getPaymentItems"
              :rules="rules.array.required"
            />
          </v-col>
          <v-col cols="12">
            <p class="catpion mb-2">カード種類</p>
            <v-btn-toggle mandatory
              v-model="form.card"
              class="transparent"
              :rules="rules.required">
              <v-btn text value="VISA">
                <v-icon :color="form.card === 'VISA' ? 'secondary' : 'blue-grey darken-4'">fab fa-cc-visa</v-icon>
              </v-btn>
              <v-btn text value="MASTER">
                <v-icon :color="form.card === 'MASTER' ? 'secondary' : 'blue-grey darken-4'">fab fa-cc-mastercard</v-icon>
              </v-btn>
              <v-btn text value="JCB">
                <v-icon :color="form.card === 'JCB' ? 'secondary' : 'blue-grey darken-4'">fab fa-cc-jcb</v-icon>
              </v-btn>
              <v-btn text value="LIFE">
                <span :class="form.card === 'LIFE' ? 'secondary--text' : 'blue-grey--text text--darken-4'">LIFE</span>
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12">
            <p class="catpion mb-2">カード名義</p>
            <v-chip-group column
              active-class="secondary white--text"
              v-model="form.holder"
              :rules="rules.required">
              <v-chip
                v-for="item in getCreditHolderItems"
                :key="`credit_holder_${item.value}`"
                :value="item.value"
                :rules="rules.required">{{ item.text }}</v-chip>
            </v-chip-group>
          </v-col>
          <v-col cols="12">
            <v-text-field validate-on-blur
              v-model="form.payment"
              label="決済金額"
              class="text-right"
              color="primary"
              counter="20"
              :rules="rules.required"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.price"
              label="お客さんへの価格"
              class="text-right"
              color="primary"
              counter="20"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.note"
              label="備考欄"
              color="primary"
              counter="50"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn text
        color="secondary"
        @click="close">閉じる</v-btn>
      <v-spacer></v-spacer>
      <v-btn depressed
        color="secondary"
        :loading="isLoading"
        :disabled="!isValid"
        @click="post">
        {{ isSetId ? '更新する' : '投稿する' }}
      </v-btn>
    </v-card-actions>
    <Confirmer ref="confirmer" />
  </v-card>
</template>

<script>
import _ from 'lodash'
import Confirmer from '@/components/modals/confirm'
export default {
  name: 'recorderCredit',
  props: [ 'client' ],
  components: {
    Confirmer
  },
  data () {
    return {
      config: {
        status: {
          loading: false,
          searching: false
        },
        datepicker: false
      },
      meta: {
        items: [
          '学費',
          '滞在費用',
          'ビザ申請料',
          '航空券代',
          '航空券変更手数料',
          '経費（贈答品、備品、接待費）他',
          '保険料',
          'ホテル代',
          '現地サポート代',
          'テスト受験代',
          '携帯電話代',
          'パソコン代',
          '受験代',
          '参考書代',
          '病院診察代',
          '現地ツアー代',
          '現地交通費',
          '現地荷物発送費',
          '制服スーツ代',
          '卒業・成績証明書類費用'
        ]
      },
      store: {
        clients: []
      },
      rules: {
        required: [
          v => !!v || '必須項目です'
        ],
        array: {
          required: [
            v => !!v.length || '必須項目'
          ]
        }
      },
      form: {
        valid: true,
        id: '',
        cid: '',
        link: true,
        date: this.$moment().format('YYYY-MM-DD'),
        payee: '',
        items: [],
        card: '',
        holder: '',
        payment: '',
        price: '',
        note: '',
        search: ''
      }
    }
  },
  methods: {
    init: function (req) {
      const { id, cid, date, content } = req || {}
      const { payee, items, card, holder, payment, price, note } = content ? JSON.parse(content) : {}
      this.$set(this.config, 'status', {
        loading: false,
        searching: false
      })
      this.$set(this, 'form', {
        valid: true,
        id: id || '',
        cid: cid || this.$route.params.id || '',
        link: true,
        date: date || this.$moment().format('YYYY-MM-DD'),
        payee: payee || '',
        items: items || [],
        card: card || '',
        holder: holder || '',
        payment: payment || '',
        price: price || '',
        note: note || ''
      })
      if (this.$route.params.id) {
        const name = this.$store.getters['client/get']('name').join(',')
        const kana = this.$store.getters['client/get']('kana').join(',')
        this.$set(this.store, 'clients', [{
          id: this.$route.params.id,
          name,
          kana
        }])
      }
    },
    close: function () {
      this.$emit('close')
    },
    post: function () {
      this.submit({ type: !this.form.id ? 'insert' : 'update' })
    },
    remove: function () {
      this.$refs.confirmer.open({
        message: 'この投稿を削除してもよろしいですか？',
        callback: () => {
          this.submit({ type: 'remove' })
        }
      })
    },
    preSubmit: function (req) {
      const { type } = req || {}
      const { id, cid, link, date, payee, items, card, holder, payment, price, note, search } = this.form
      const aid = this.$store.getters['author/get']('userid')
      const developer = this.$store.getters['author/get']('roles').includes('developer')
      const urls = {
        insert: `${this.$url.api}posts/insert/credit/${cid}`,
        update: `${this.$url.api}posts/update/credit/${id}`,
        remove: `${this.$url.api}posts/remove/credit/${id}`,
        client: `${this.$url.api}client/get/items`,
      }
      const fds = {
        insert: { aid, link, date, payee, items, card, holder, payment, price, note },
        update: { cid, aid, link, date, payee, items, card, holder, payment, price, note },
        remove: { cid, aid },
        client: { name: search }
      }
      if (type && urls[type] && fds[type]) {
        if ([ 'insert', 'update' ].includes(type)) {
          const url = urls[type]
          const fd = fds[type]
          if (this.$refs.form.validate()) {
            this.$set(this.config.status, 'loading', true)
            return { url, fd, type }
          } else {
            return false
          }
        } else if (type === 'client' && search) {
          this.$set(this.config.status, 'searching', true)
          return { type, url: urls[type], fd: fds[type] }
        } else {
          return false
        }
      } else {
        return false
      }
    },
    submit: function (req) {
      const dt = this.preSubmit(req)
      if (dt) {
        const { url, fd, type } = dt
        this.$firebase.auth().currentUser.getIdToken(true).then(token => {
          this.axios.post(url, fd, {
            headers: {
              'firebase-token': token
            }
          }).then(res => {
            console.log(res)
            if (res.data && res.data.success) {
              this.postSubmit(res.data.data, type)
            } else {
              this.failSubmit()
            }
          }).catch(err => {
            console.log(err)
            this.failSubmit()
          })
        })
      }
    },
    postSubmit: function (req, postType) {
      const socket = this.$store.getters['config/get']('socket')
      const type = postType || ''
      if (type === 'client') {
        this.$set(this.config.status, 'searching', false)
        this.$set(this.store, 'clients', req)
      } else {
        if (!socket.connected) {
          socket.connect()
        }
        socket.emit('POST_POST_CREDIT', req, (res) => {
          console.log(res)
          this.$emit('done', req)
          this.init()
          this.$set(this.config.status, 'loading', false)
        })
      }
    },
    failSubmit: function (req) {
      this.$set(this.config.status, 'loading', false)
    },
    searchClient: _.debounce(function (val) {
      this.submit({ type: 'client' })
    }, 300),
  },
  watch: {
    'form.search': function (newVal, oldVal) {
      if (!this.$route.params.id) {
        if (newVal && oldVal && newVal.length < oldVal.length) {
          this.$set(this.store, 'clients', [])
        }
        if (!this.config.status.searching && !this.store.clients.length) {
          this.searchClient()
        }
      }
    }
  },
  computed: {
    isValid () {
      return this.form.valid
    },
    isLoading () {
      return this.config.status.loading
    },
    isSearching () {
      return this.config.status.searching
    },
    isSetId () {
      return this.form.id
    },
    isLink () {
      return this.form.link
    },
    isSetClient () {
      return !!this.$route.params.id
    },
    printClientName () {
      return this.$store.getters['client/get']('name').join(' ')
    },
    printFormDate () {
      return this.form.date ? this.$moment(this.form.date).format('YYYY/MM/DD') : ''
    },
    printNoDataText () {
      return this.form.search ? '検索結果0件です' : 'ふりがな or 名前を入力してください'
    },
    getPaymentItems () {
      return this.meta.items
    },
    getClientItems () {
      return this.store.clients.length ? this.store.clients.map(it => {
        return {
          text: `${it.name.replace(',', '')}（${it.kana.replace(',', '')}）`,
          value: it.id
        }
      }) : []
    },
    getCreditHolderItems () {
      const authors = this.$store.getters['config/get']('authors')
      const keys = Object.keys(authors)
      return keys.filter(key => { return authors[key].roles.includes('credit') }).map(key => {
        return {
          text: authors[key].name[0],
          value: authors[key].name[0]
        }
      })
    }
  }
}
</script>